import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Server, Container, Check, Cpu, HardDrive, Clock, DollarSign, Settings2, ChevronUp, ChevronDown, Network, Shield, Key, Bell, Tag, AlertTriangle, Zap } from 'lucide-react';
import clsx from 'clsx';
import { SubscriptionRequiredModal } from './SubscriptionRequiredModal';
import { billingApi, SubscriptionStatus } from '../services/api';

// AWS on-demand prices (us-east-1, Feb 2026). Service markup ~25% over AWS on-demand.
// Spot estimates based on typical ~65% savings over service on-demand rate.
const INSTANCE_TYPES = [
  // Free Tier (no GPU)
  { type: 't2.micro',    gpu: null,          gpus: 0, gpuMemory: 0,  vCpus: 1,  memory: 1,   nvmeStorage: 0,    network: 'Low to Moderate', freeTier: true,  awsOnDemandPerHr: 0,     serviceOnDemandPerHr: 0,    serviceSpotPerHr: 0    },
  { type: 't3.micro',    gpu: null,          gpus: 0, gpuMemory: 0,  vCpus: 2,  memory: 1,   nvmeStorage: 0,    network: 'Up to 5 Gbps',   freeTier: true,  awsOnDemandPerHr: 0,     serviceOnDemandPerHr: 0,    serviceSpotPerHr: 0    },
  // GPU Instances (sorted by on-demand price ascending)
  { type: 'g4dn.xlarge', gpu: 'NVIDIA T4',   gpus: 1, gpuMemory: 16, vCpus: 4,  memory: 16,  nvmeStorage: 125,  network: 'Up to 25 Gbps',  freeTier: false, awsOnDemandPerHr: 0.526, serviceOnDemandPerHr: 0.65, serviceSpotPerHr: 0.22 },
  { type: 'g5.xlarge',   gpu: 'NVIDIA A10G', gpus: 1, gpuMemory: 24, vCpus: 4,  memory: 16,  nvmeStorage: 250,  network: 'Up to 10 Gbps',  freeTier: false, awsOnDemandPerHr: 1.006, serviceOnDemandPerHr: 1.25, serviceSpotPerHr: 0.42 },
  { type: 'g5.2xlarge',  gpu: 'NVIDIA A10G', gpus: 1, gpuMemory: 24, vCpus: 8,  memory: 32,  nvmeStorage: 450,  network: 'Up to 10 Gbps',  freeTier: false, awsOnDemandPerHr: 1.212, serviceOnDemandPerHr: 1.52, serviceSpotPerHr: 0.50 },
  { type: 'g5.4xlarge',  gpu: 'NVIDIA A10G', gpus: 1, gpuMemory: 24, vCpus: 16, memory: 64,  nvmeStorage: 600,  network: 'Up to 25 Gbps',  freeTier: false, awsOnDemandPerHr: 1.624, serviceOnDemandPerHr: 2.03, serviceSpotPerHr: 0.68 },
  { type: 'g5.8xlarge',  gpu: 'NVIDIA A10G', gpus: 1, gpuMemory: 24, vCpus: 32, memory: 128, nvmeStorage: 900,  network: '25 Gbps',         freeTier: false, awsOnDemandPerHr: 2.448, serviceOnDemandPerHr: 3.06, serviceSpotPerHr: 1.02 },
  { type: 'g5.12xlarge', gpu: 'NVIDIA A10G', gpus: 4, gpuMemory: 96, vCpus: 48, memory: 192, nvmeStorage: 3800, network: '40 Gbps',         freeTier: false, awsOnDemandPerHr: 5.672, serviceOnDemandPerHr: 7.09, serviceSpotPerHr: 2.38 },
  { type: 'p3.2xlarge',  gpu: 'NVIDIA V100', gpus: 1, gpuMemory: 16, vCpus: 8,  memory: 61,  nvmeStorage: 0,    network: 'Up to 10 Gbps',  freeTier: false, awsOnDemandPerHr: 3.06,  serviceOnDemandPerHr: 3.83, serviceSpotPerHr: 1.28 },
];

const HOURS_PER_MONTH = 730;

const formatStorage = (gb: number) => {
  if (gb === 0) return 'EBS Only';
  return gb >= 1000 ? `${(gb / 1000).toFixed(1)} TB NVMe` : `${gb} GB NVMe`;
};

// Pre-configured Docker images for ML/AI workloads
const DOCKER_IMAGES = [
  { value: '', label: 'Default CUDA Image', description: 'nvidia/cuda:12.2.0-base-ubuntu22.04' },
  { value: 'pytorch/pytorch:2.1.0-cuda12.1-cudnn8-runtime', label: 'PyTorch 2.1', description: 'CUDA 12.1 + cuDNN 8' },
  { value: 'pytorch/pytorch:2.0.1-cuda11.7-cudnn8-runtime', label: 'PyTorch 2.0', description: 'CUDA 11.7 + cuDNN 8' },
  { value: 'tensorflow/tensorflow:2.14.0-gpu', label: 'TensorFlow 2.14', description: 'GPU enabled' },
  { value: 'tensorflow/tensorflow:2.13.0-gpu', label: 'TensorFlow 2.13', description: 'GPU enabled' },
  { value: 'huggingface/transformers-pytorch-gpu:latest', label: 'HuggingFace Transformers', description: 'PyTorch GPU' },
  { value: 'nvcr.io/nvidia/pytorch:23.10-py3', label: 'NVIDIA PyTorch NGC', description: 'Optimized container' },
  { value: 'nvcr.io/nvidia/tensorflow:23.10-tf2-py3', label: 'NVIDIA TensorFlow NGC', description: 'Optimized container' },
  { value: 'jupyter/tensorflow-notebook:latest', label: 'Jupyter + TensorFlow', description: 'Notebook environment' },
  { value: 'jupyter/pytorch-notebook:latest', label: 'Jupyter + PyTorch', description: 'Notebook environment' },
  { value: 'custom', label: 'Custom Image...', description: 'Enter custom Docker image' },
];

// Volume size presets
const VOLUME_SIZES = [
  { value: 50, label: '50 GB', description: 'Small workloads' },
  { value: 100, label: '100 GB', description: 'Standard (default)' },
  { value: 200, label: '200 GB', description: 'Medium datasets' },
  { value: 500, label: '500 GB', description: 'Large datasets' },
  { value: 1000, label: '1 TB', description: 'Very large datasets' },
];

// Idle timeout presets
const IDLE_TIMEOUTS = [
  { value: 0, label: 'Disabled' },
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

// Max cost presets
const MAX_COST_PRESETS = [
  { value: 0, label: 'No limit' },
  { value: 5, label: '$5' },
  { value: 10, label: '$10' },
  { value: 25, label: '$25' },
  { value: 50, label: '$50' },
  { value: 100, label: '$100' },
  { value: 250, label: '$250' },
  { value: 500, label: '$500' },
];

type DeploymentType = 'instance' | 'container';

// Availability zone options
const AVAILABILITY_ZONES = [
  { value: '', label: 'Auto (any AZ)' },
  { value: 'us-east-1a', label: 'us-east-1a' },
  { value: 'us-east-1b', label: 'us-east-1b' },
  { value: 'us-east-1c', label: 'us-east-1c' },
  { value: 'us-east-1d', label: 'us-east-1d' },
  { value: 'us-east-1e', label: 'us-east-1e' },
  { value: 'us-east-1f', label: 'us-east-1f' },
  { value: 'us-west-2a', label: 'us-west-2a' },
  { value: 'us-west-2b', label: 'us-west-2b' },
  { value: 'us-west-2c', label: 'us-west-2c' },
  { value: 'us-west-2d', label: 'us-west-2d' },
];

interface GpuInstanceConfig {
  // Basic Configuration
  instanceType: string;
  useSpot: boolean;
  maxRuntimeHours: number;
  environment: 'development' | 'staging' | 'production';
  project: string;
  
  // Storage Configuration
  rootVolumeSize: number;
  rootVolumeType: 'gp3' | 'gp2' | 'io1' | 'io2';
  useInstanceStore: boolean;
  
  // Network Configuration
  assignPublicIp: boolean;
  networkMode: 'shared' | 'dedicated' | 'existing';
  availabilityZone: string;
  existingVpcId: string;
  existingSubnetId: string;
  existingSecurityGroupId: string;
  
  // Spot Configuration
  spotInterruptionBehavior: 'terminate' | 'stop' | 'hibernate';
  spotFallbackToOnDemand: boolean;
  
  // Container Configuration
  dockerImagePreset: string;
  customDockerImage: string;
  startupCommand: string;
  
  // Lifecycle Configuration
  idleTimeoutMinutes: number;
  requireHeartbeat: boolean;
  
  // Notifications
  notifyOnComplete: boolean;
  notifyOnFailure: boolean;
  
  // Resource Limits
  maxCostUsd: number;
  
  // Metadata
  name: string;
  
  // Security
  sshKeyMode: 'generate' | 'none' | 'existing';
  existingSshKeyName: string;
}

interface ContainerAdvancedConfig {
  dockerImage: string;
  customDockerImage: string;
  startupCommand: string;
  environmentVariables: string;
  containerPort: number;
  hostPort: number;
  taskCpu: string;
  taskMemory: string;
  healthCheckPath: string;
  healthCheckIntervalSeconds: number;
  healthCheckTimeoutSeconds: number;
  healthCheckRetries: number;
  logRetentionDays: number;
  idleTimeoutMinutes: number;
  requireHeartbeat: boolean;
  maxCostUsd: number;
  notifyOnComplete: boolean;
  notifyOnFailure: boolean;
}

interface DeploymentFormData {
  name: string;
  slug: string;
  type: 'PERSONAL' | 'TEAM' | 'ENTERPRISE';
  deploymentType: DeploymentType | null;
  gpuInstanceConfig: GpuInstanceConfig;
  // GPU Container configuration (ECS)
  containerInstanceType: string;
  maxInstances: number;
  desiredCount: number;
  useSpot: boolean;
  gracePeriod: number;
  checkInterval: number;
  containerAdvanced: ContainerAdvancedConfig;
}

interface CreateDeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DEFAULT_GPU_INSTANCE_CONFIG: GpuInstanceConfig = {
  instanceType: 'g4dn.xlarge',
  useSpot: true,
  maxRuntimeHours: 4,
  environment: 'development',
  project: '',
  rootVolumeSize: 100,
  rootVolumeType: 'gp3',
  useInstanceStore: false,
  assignPublicIp: true,
  networkMode: 'shared',
  availabilityZone: '',
  existingVpcId: '',
  existingSubnetId: '',
  existingSecurityGroupId: '',
  spotInterruptionBehavior: 'terminate',
  spotFallbackToOnDemand: false,
  dockerImagePreset: '',
  customDockerImage: '',
  startupCommand: '',
  idleTimeoutMinutes: 0,
  requireHeartbeat: false,
  notifyOnComplete: false,
  notifyOnFailure: false,
  maxCostUsd: 0,
  name: '',
  sshKeyMode: 'generate',
  existingSshKeyName: '',
};

export function CreateDeploymentModal({ isOpen, onClose, onSuccess }: CreateDeploymentModalProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAdvancedContainer, setShowAdvancedContainer] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [billingStatus, setBillingStatus] = useState<SubscriptionStatus | null>(null);
  const [billingCheckDone, setBillingCheckDone] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<{
    message?: string;
    action?: 'add_funds' | 'increase_limit' | 'add_payment_method';
  }>({});

  useEffect(() => {
    if (isOpen) {
      setBillingCheckDone(false);
      billingApi.getSubscriptionStatus()
        .then((status) => {
          setBillingStatus(status);
          setBillingCheckDone(true);
          if (!status.canDeploy) {
            setSubscriptionError({
              message: status.message || undefined,
              action: status.requiredAction || 'add_payment_method',
            });
            setShowSubscriptionModal(true);
          }
        })
        .catch(() => {
          setBillingCheckDone(true);
        });
    }
  }, [isOpen]);
  
  const [formData, setFormData] = useState<DeploymentFormData>({
    name: '',
    slug: '',
    type: 'TEAM',
    deploymentType: null,
    
    // GPU Instance defaults (EC2) - matching Admin dashboard exactly
    gpuInstanceConfig: { ...DEFAULT_GPU_INSTANCE_CONFIG },
    
    // Container defaults (ECS)
    containerInstanceType: 'g4dn.xlarge',
    maxInstances: 2,
    desiredCount: 0,
    useSpot: true,
    gracePeriod: 60,
    checkInterval: 60,
    containerAdvanced: {
      dockerImage: '',
      customDockerImage: '',
      startupCommand: '',
      environmentVariables: '',
      containerPort: 8080,
      hostPort: 0,
      taskCpu: '4096',
      taskMemory: '16384',
      healthCheckPath: '',
      healthCheckIntervalSeconds: 30,
      healthCheckTimeoutSeconds: 5,
      healthCheckRetries: 3,
      logRetentionDays: 30,
      idleTimeoutMinutes: 0,
      requireHeartbeat: false,
      maxCostUsd: 0,
      notifyOnComplete: false,
      notifyOnFailure: true,
    },
  });

  const totalSteps = 3;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const updateFormData = (updates: Partial<DeploymentFormData>) => {
    setFormData({ ...formData, ...updates });
  };

  const updateGpuConfig = (updates: Partial<GpuInstanceConfig>) => {
    setFormData({
      ...formData,
      gpuInstanceConfig: { ...formData.gpuInstanceConfig, ...updates },
    });
  };

  const updateContainerAdvanced = (updates: Partial<ContainerAdvancedConfig>) => {
    setFormData({
      ...formData,
      containerAdvanced: { ...formData.containerAdvanced, ...updates },
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let infrastructureConfig: any;
      
      if (formData.deploymentType === 'instance') {
        // GPU Instance config - matching Admin dashboard CreateGpuJobDto format exactly
        const cfg = formData.gpuInstanceConfig;
        const dockerImage = cfg.dockerImagePreset === 'custom' 
          ? cfg.customDockerImage 
          : cfg.dockerImagePreset;

        infrastructureConfig = {
          instanceType: cfg.instanceType,
          useSpot: cfg.useSpot,
          maxRuntimeHours: cfg.maxRuntimeHours,
          environment: cfg.environment,
        };

        // Add optional basic fields
        if (cfg.project) infrastructureConfig.project = cfg.project;

        // Add advanced options if they were opened
        if (showAdvanced) {
          // Storage
          if (cfg.rootVolumeSize !== 100) infrastructureConfig.rootVolumeSize = cfg.rootVolumeSize;
          if (cfg.rootVolumeType !== 'gp3') infrastructureConfig.rootVolumeType = cfg.rootVolumeType;
          if (cfg.useInstanceStore) infrastructureConfig.useInstanceStore = cfg.useInstanceStore;

          // Networking
          if (!cfg.assignPublicIp) infrastructureConfig.assignPublicIp = cfg.assignPublicIp;
          infrastructureConfig.networkMode = cfg.networkMode;
          infrastructureConfig.useDedicatedNetwork = cfg.networkMode === 'dedicated';
          if (cfg.networkMode === 'existing') {
            infrastructureConfig.existingVpcId = cfg.existingVpcId;
            infrastructureConfig.existingSubnetId = cfg.existingSubnetId;
            infrastructureConfig.existingSecurityGroupId = cfg.existingSecurityGroupId;
          }
          if (cfg.availabilityZone) infrastructureConfig.availabilityZone = cfg.availabilityZone;

          // Spot
          if (cfg.spotInterruptionBehavior !== 'terminate') infrastructureConfig.spotInterruptionBehavior = cfg.spotInterruptionBehavior;
          if (cfg.spotFallbackToOnDemand) infrastructureConfig.spotFallbackToOnDemand = cfg.spotFallbackToOnDemand;

          // Container
          if (dockerImage) infrastructureConfig.dockerImage = dockerImage;
          if (cfg.startupCommand) infrastructureConfig.startupCommand = cfg.startupCommand;

          // Lifecycle
          if (cfg.idleTimeoutMinutes > 0) infrastructureConfig.idleTimeoutMinutes = cfg.idleTimeoutMinutes;
          if (cfg.requireHeartbeat) infrastructureConfig.requireHeartbeat = cfg.requireHeartbeat;

          // Notifications
          if (cfg.notifyOnComplete) infrastructureConfig.notifyOnComplete = cfg.notifyOnComplete;
          if (cfg.notifyOnFailure) infrastructureConfig.notifyOnFailure = cfg.notifyOnFailure;

          // Resource Limits
          if (cfg.maxCostUsd > 0) infrastructureConfig.maxCostUsd = cfg.maxCostUsd;

          // Metadata
          if (cfg.name) infrastructureConfig.name = cfg.name;

          // Security
          if (cfg.sshKeyMode !== 'generate') infrastructureConfig.sshKeyMode = cfg.sshKeyMode;
          if (cfg.sshKeyMode === 'existing' && cfg.existingSshKeyName) {
            infrastructureConfig.existingSshKeyName = cfg.existingSshKeyName;
          }
        }
      } else if (formData.deploymentType === 'container') {
        const adv = formData.containerAdvanced;
        const dockerImg = adv.dockerImage === 'custom' ? adv.customDockerImage : adv.dockerImage;
        const envVars: Record<string, string> = {};
        if (adv.environmentVariables.trim()) {
          adv.environmentVariables.trim().split('\n').forEach(line => {
            const eqIdx = line.indexOf('=');
            if (eqIdx > 0) envVars[line.substring(0, eqIdx).trim()] = line.substring(eqIdx + 1).trim();
          });
        }

        infrastructureConfig = {
          instanceType: formData.containerInstanceType,
          maxInstances: formData.maxInstances,
          desiredCount: formData.desiredCount,
          useSpot: formData.useSpot,
          gracePeriod: formData.gracePeriod,
          checkInterval: formData.checkInterval,
          ...(dockerImg && { dockerImage: dockerImg }),
          ...(adv.startupCommand && { startupCommand: adv.startupCommand }),
          ...(Object.keys(envVars).length > 0 && { environmentVariables: envVars }),
          containerPort: adv.containerPort,
          hostPort: adv.hostPort,
          taskCpu: adv.taskCpu,
          taskMemory: adv.taskMemory,
          ...(adv.healthCheckPath && {
            healthCheckPath: adv.healthCheckPath,
            healthCheckIntervalSeconds: adv.healthCheckIntervalSeconds,
            healthCheckTimeoutSeconds: adv.healthCheckTimeoutSeconds,
            healthCheckRetries: adv.healthCheckRetries,
          }),
          logRetentionDays: adv.logRetentionDays,
          idleTimeoutMinutes: adv.idleTimeoutMinutes,
          requireHeartbeat: adv.requireHeartbeat,
          maxCostUsd: adv.maxCostUsd,
          notifyOnComplete: adv.notifyOnComplete,
          notifyOnFailure: adv.notifyOnFailure,
        };
      }

      const response = await fetch('/api/workspaces', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          type: formData.type,
          deploymentType: formData.deploymentType || undefined,
          infrastructureConfig,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle billing requirement errors
        if (response.status === 402) {
          setSubscriptionError({
            message: errorData.message || 'Funds required',
            action: errorData.requiresAction || 'add_funds',
          });
          setShowSubscriptionModal(true);
          return;
        }
        
        throw new Error(errorData.message || 'Failed to create deployment');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to create deployment:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create deployment. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setShowAdvanced(false);
    setFormData({
      name: '',
      slug: '',
      type: 'TEAM',
      deploymentType: null,
      gpuInstanceConfig: { ...DEFAULT_GPU_INSTANCE_CONFIG },
      containerInstanceType: 'g4dn.xlarge',
      maxInstances: 2,
      desiredCount: 0,
      useSpot: true,
      gracePeriod: 60,
      checkInterval: 60,
    });
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.name && formData.slug;
      case 2:
        return formData.deploymentType !== null;
      case 3: {
        if (formData.deploymentType === 'instance' && showAdvanced && formData.gpuInstanceConfig.networkMode === 'existing') {
          const cfg = formData.gpuInstanceConfig;
          return !!(cfg.existingVpcId && cfg.existingSubnetId && cfg.existingSecurityGroupId);
        }
        return true;
      }
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Subscription Required Modal */}
      <SubscriptionRequiredModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        message={subscriptionError.message}
        action={subscriptionError.action}
      />

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background-secondary border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Create Deployment</h2>
            <p className="text-sm text-foreground-secondary mt-1">
              Step {step} of {totalSteps}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-foreground-secondary hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={clsx(
                  'flex-1 h-2 rounded-full transition-colors',
                  s <= step ? 'bg-accent' : 'bg-background-tertiary'
                )}
              />
            ))}
          </div>
        </div>

        {/* Billing Warning Banner */}
        {billingCheckDone && billingStatus && !billingStatus.canDeploy && (
          <div className="mx-6 mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                  {!billingStatus.hasPaymentMethod
                    ? 'Card details required to create deployments'
                    : 'Insufficient funds to create deployments'}
                </p>
                <p className="text-xs text-foreground-secondary mt-1">
                  {billingStatus.message || 'Please visit the Billing page to set up your payment method and add funds.'}
                </p>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="mt-2 text-xs font-medium text-accent hover:text-accent/80 transition-colors underline"
                >
                  Go to Billing
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Deployment Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-secondary mb-2">
                      Deployment Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateFormData({ name: e.target.value })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="My GPU Deployment"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-secondary mb-2">
                      Slug
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) =>
                        updateFormData({
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                        })
                      }
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="my-gpu-deployment"
                      pattern="[a-z0-9\-]+"
                    />
                    <p className="mt-1 text-xs text-foreground-tertiary">
                      Lowercase letters, numbers, and hyphens only
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-secondary mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        updateFormData({
                          type: e.target.value as 'PERSONAL' | 'TEAM' | 'ENTERPRISE',
                        })
                      }
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="PERSONAL">Personal</option>
                      <option value="TEAM">Team</option>
                      <option value="ENTERPRISE">Enterprise</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Choose Deployment Type */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Choose Infrastructure Type
                </h3>
                <p className="text-sm text-foreground-secondary mb-6">
                  Select how you want to deploy your GPU workload
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* GPU Instance */}
                  <button
                    onClick={() => updateFormData({ deploymentType: 'instance' })}
                    className={clsx(
                      'relative p-6 rounded-lg border-2 transition-all text-left',
                      formData.deploymentType === 'instance'
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50 bg-background'
                    )}
                  >
                    {formData.deploymentType === 'instance' && (
                      <div className="absolute top-4 right-4">
                        <Check className="w-6 h-6 text-accent" />
                      </div>
                    )}
                    <Server className="w-12 h-12 text-accent mb-4" />
                    <h4 className="text-lg font-semibold text-foreground mb-2">
                      GPU Instance
                    </h4>
                    <p className="text-sm text-foreground-secondary mb-4">
                      Dedicated EC2 instance with direct GPU access. Best for long-running
                      workloads and custom environments.
                    </p>
                    <ul className="space-y-1 text-sm text-foreground-tertiary">
                      <li>• Full GPU control</li>
                      <li>• Persistent storage</li>
                      <li>• SSH access</li>
                      <li>• Custom configuration</li>
                    </ul>
                  </button>

                  {/* GPU Container */}
                  <button
                    onClick={() => updateFormData({ deploymentType: 'container' })}
                    className={clsx(
                      'relative p-6 rounded-lg border-2 transition-all text-left',
                      formData.deploymentType === 'container'
                        ? 'border-accent bg-accent/5'
                        : 'border-border hover:border-accent/50 bg-background'
                    )}
                  >
                    {formData.deploymentType === 'container' && (
                      <div className="absolute top-4 right-4">
                        <Check className="w-6 h-6 text-accent" />
                      </div>
                    )}
                    <Container className="w-12 h-12 text-accent mb-4" />
                    <h4 className="text-lg font-semibold text-foreground mb-2">
                      GPU Container
                    </h4>
                    <p className="text-sm text-foreground-secondary mb-4">
                      Auto-scaling ECS container with GPU support. Best for batch processing
                      and scalable workloads.
                    </p>
                    <ul className="space-y-1 text-sm text-foreground-tertiary">
                      <li>• Auto-scaling</li>
                      <li>• Docker-based</li>
                      <li>• Pay per use</li>
                      <li>• Easy deployment</li>
                    </ul>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Configuration */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {formData.deploymentType === 'instance'
                    ? 'GPU Instance Configuration'
                    : 'GPU Container Configuration'}
                </h3>
                <p className="text-sm text-foreground-secondary mb-6">
                  Configure your {formData.deploymentType === 'instance' ? 'instance' : 'container'} settings
                </p>

                {formData.deploymentType === 'instance' ? (
                  <div className="space-y-6">
                    {/* Instance Type Selection */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-accent" />
                        Select Instance
                      </h4>

                      {/* Table header (visible on md+) */}
                      <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-4 px-4 pb-2 text-xs text-foreground-tertiary uppercase tracking-wider border-b border-border mb-2">
                        <span>GPU</span>
                        <span className="w-16 text-right">VRAM</span>
                        <span className="w-14 text-right">vCPU</span>
                        <span className="w-16 text-right">RAM</span>
                        <span className="w-20 text-right">Storage</span>
                        <span className="w-24 text-right">Price</span>
                      </div>

                      {/* Free Tier */}
                      <p className="text-xs text-foreground-tertiary uppercase tracking-wider mt-3 mb-1.5 px-1">Free Tier</p>
                      <div className="space-y-1.5 mb-4">
                        {INSTANCE_TYPES.filter(i => i.freeTier).map((inst) => (
                          <button
                            key={inst.type}
                            type="button"
                            onClick={() => updateGpuConfig({ instanceType: inst.type })}
                            className={clsx(
                              'w-full rounded-lg border-2 text-left transition-all relative',
                              formData.gpuInstanceConfig.instanceType === inst.type
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-border hover:border-emerald-500/50 bg-background',
                            )}
                          >
                            {/* Desktop row */}
                            <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-4 items-center px-4 py-3">
                              <div>
                                <span className="font-medium text-foreground">CPU Only</span>
                                <span className="ml-2 font-mono text-xs text-foreground-tertiary">{inst.type}</span>
                              </div>
                              <span className="w-16 text-right text-sm text-foreground-tertiary">—</span>
                              <span className="w-14 text-right text-sm text-foreground-secondary">{inst.vCpus}</span>
                              <span className="w-16 text-right text-sm text-foreground-secondary">{inst.memory} GB</span>
                              <span className="w-20 text-right text-sm text-foreground-tertiary">EBS</span>
                              <span className="w-24 text-right font-semibold text-emerald-500">FREE</span>
                            </div>
                            {/* Mobile card */}
                            <div className="md:hidden px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-foreground">CPU Only</span>
                                  <span className="ml-2 font-mono text-xs text-foreground-tertiary">{inst.type}</span>
                                </div>
                                <span className="font-semibold text-emerald-500">FREE</span>
                              </div>
                              <div className="text-xs text-foreground-tertiary mt-1">
                                {inst.vCpus} vCPU · {inst.memory} GB RAM · EBS Only
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* GPU Instances */}
                      <p className="text-xs text-foreground-tertiary uppercase tracking-wider mb-1.5 px-1">GPU Instances</p>
                      <div className="space-y-1.5">
                        {INSTANCE_TYPES.filter(i => !i.freeTier).map((inst) => (
                          <button
                            key={inst.type}
                            type="button"
                            onClick={() => updateGpuConfig({ instanceType: inst.type })}
                            className={clsx(
                              'w-full rounded-lg border-2 text-left transition-all',
                              formData.gpuInstanceConfig.instanceType === inst.type
                                ? 'border-accent bg-accent/5 shadow-sm shadow-accent/10'
                                : 'border-border hover:border-accent/50 bg-background',
                            )}
                          >
                            {/* Desktop row */}
                            <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-4 items-center px-4 py-3">
                              <div>
                                <span className="font-semibold text-foreground">{inst.gpu}</span>
                                {inst.gpus > 1 && (
                                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-accent/20 text-accent font-medium">{inst.gpus}x</span>
                                )}
                                <span className="ml-2 font-mono text-xs text-foreground-tertiary">{inst.type}</span>
                              </div>
                              <span className="w-16 text-right text-sm text-foreground-secondary">{inst.gpuMemory} GB</span>
                              <span className="w-14 text-right text-sm text-foreground-secondary">{inst.vCpus}</span>
                              <span className="w-16 text-right text-sm text-foreground-secondary">{inst.memory} GB</span>
                              <span className="w-20 text-right text-sm text-foreground-tertiary">{formatStorage(inst.nvmeStorage)}</span>
                              <div className="w-24 text-right">
                                <div className="text-base font-bold text-foreground">${inst.serviceOnDemandPerHr.toFixed(2)}<span className="text-xs font-normal text-foreground-secondary"> /hr</span></div>
                                <div className="text-xs text-foreground-tertiary">~${Math.round(inst.serviceOnDemandPerHr * HOURS_PER_MONTH)} /mo</div>
                              </div>
                            </div>
                            {/* Mobile card */}
                            <div className="md:hidden px-4 py-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-foreground">{inst.gpu}</span>
                                  {inst.gpus > 1 && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-accent/20 text-accent font-medium">{inst.gpus}x</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  <span className="text-base font-bold text-foreground">${inst.serviceOnDemandPerHr.toFixed(2)}</span>
                                  <span className="text-xs text-foreground-secondary"> /hr</span>
                                </div>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="font-mono text-xs text-foreground-tertiary">{inst.type}</span>
                                <span className="text-xs text-foreground-tertiary">~${Math.round(inst.serviceOnDemandPerHr * HOURS_PER_MONTH)} /mo</span>
                              </div>
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5 text-xs text-foreground-secondary">
                                <span>{inst.gpuMemory} GB VRAM</span>
                                <span className="text-foreground-tertiary">·</span>
                                <span>{inst.vCpus} vCPU</span>
                                <span className="text-foreground-tertiary">·</span>
                                <span>{inst.memory} GB RAM</span>
                                <span className="text-foreground-tertiary">·</span>
                                <span>{formatStorage(inst.nvmeStorage)}</span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Runtime & Environment */}
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-accent" />
                        Configuration
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-foreground-secondary mb-2">Max Runtime (hours) *</label>
                          <input
                            type="number"
                            min={1}
                            max={24}
                            value={formData.gpuInstanceConfig.maxRuntimeHours}
                            onChange={(e) => updateGpuConfig({ maxRuntimeHours: parseInt(e.target.value) || 1 })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                          <p className="text-xs text-foreground-tertiary mt-1">
                            Instance will be terminated after this duration
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm text-foreground-secondary mb-2">Environment *</label>
                          <select
                            value={formData.gpuInstanceConfig.environment}
                            onChange={(e) => updateGpuConfig({ environment: e.target.value as 'development' | 'staging' | 'production' })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          >
                            <option value="development">Development</option>
                            <option value="staging">Staging</option>
                            <option value="production">Production</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-foreground-secondary mb-2">Project (optional)</label>
                          <input
                            type="text"
                            value={formData.gpuInstanceConfig.project}
                            onChange={(e) => updateGpuConfig({ project: e.target.value })}
                            placeholder="e.g., ml-training"
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-foreground-secondary mb-2">Pricing Model *</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => updateGpuConfig({ useSpot: true })}
                              className={clsx(
                                'flex-1 px-4 py-2 rounded-lg border-2 transition-all',
                                formData.gpuInstanceConfig.useSpot
                                  ? 'border-accent bg-accent/10 text-accent'
                                  : 'border-border text-foreground-secondary',
                              )}
                            >
                              Spot (~70% off)
                            </button>
                            <button
                              type="button"
                              onClick={() => updateGpuConfig({ useSpot: false })}
                              disabled={formData.gpuInstanceConfig.environment !== 'production'}
                              className={clsx(
                                'flex-1 px-4 py-2 rounded-lg border-2 transition-all',
                                !formData.gpuInstanceConfig.useSpot
                                  ? 'border-accent bg-accent/10 text-accent'
                                  : 'border-border text-foreground-secondary',
                                formData.gpuInstanceConfig.environment !== 'production' && 'opacity-50 cursor-not-allowed',
                              )}
                            >
                              On-Demand
                            </button>
                          </div>
                          {formData.gpuInstanceConfig.environment !== 'production' && (
                            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              On-demand only available for production
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Advanced Options Toggle */}
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full flex items-center justify-between p-4 rounded-lg border border-border hover:border-accent/50 hover:bg-background transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Settings2 className="w-5 h-5 text-accent" />
                          <span className="font-semibold text-foreground">Advanced Options</span>
                          <span className="text-xs text-foreground-tertiary">(Container, Storage, Network, Security & more)</span>
                        </div>
                        {showAdvanced ? (
                          <ChevronUp className="w-5 h-5 text-foreground-secondary" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-foreground-secondary" />
                        )}
                      </button>
                    </div>

                    {/* Advanced Options Sections */}
                    {showAdvanced && (
                      <div className="space-y-6 p-4 rounded-lg border border-border bg-background">
                        {/* Container & Image */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Container className="w-4 h-4 text-accent" />
                            Container Image
                          </h4>
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {DOCKER_IMAGES.map((image) => (
                                <button
                                  key={image.value}
                                  type="button"
                                  onClick={() => updateGpuConfig({ dockerImagePreset: image.value })}
                                  className={clsx(
                                    'p-3 rounded-lg border-2 text-left transition-all',
                                    formData.gpuInstanceConfig.dockerImagePreset === image.value
                                      ? 'border-accent bg-accent/10'
                                      : 'border-border hover:border-accent/50 bg-background',
                                  )}
                                >
                                  <div className="text-sm font-medium text-foreground">{image.label}</div>
                                  <div className="text-xs text-foreground-tertiary mt-0.5 truncate">{image.description}</div>
                                </button>
                              ))}
                            </div>
                            {formData.gpuInstanceConfig.dockerImagePreset === 'custom' && (
                              <div>
                                <label className="block text-sm text-foreground-secondary mb-2">Custom Docker Image</label>
                                <input
                                  type="text"
                                  value={formData.gpuInstanceConfig.customDockerImage}
                                  onChange={(e) => updateGpuConfig({ customDockerImage: e.target.value })}
                                  placeholder="e.g., myregistry/myimage:latest"
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                            )}
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Startup Command (optional)</label>
                              <input
                                type="text"
                                value={formData.gpuInstanceConfig.startupCommand}
                                onChange={(e) => updateGpuConfig({ startupCommand: e.target.value })}
                                placeholder="e.g., python train.py --epochs 100"
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Storage */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <HardDrive className="w-4 h-4 text-accent" />
                            Storage
                          </h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Root Volume Size</label>
                              <div className="grid grid-cols-5 gap-1">
                                {VOLUME_SIZES.map((size) => (
                                  <button
                                    key={size.value}
                                    type="button"
                                    onClick={() => updateGpuConfig({ rootVolumeSize: size.value })}
                                    className={clsx(
                                      'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                      formData.gpuInstanceConfig.rootVolumeSize === size.value
                                        ? 'border-accent bg-accent/10 text-accent'
                                        : 'border-border hover:border-accent/50 text-foreground-secondary',
                                    )}
                                  >
                                    <div className="text-sm font-medium">{size.label}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Volume Type</label>
                              <select
                                value={formData.gpuInstanceConfig.rootVolumeType}
                                onChange={(e) => updateGpuConfig({ rootVolumeType: e.target.value as 'gp3' | 'gp2' | 'io1' | 'io2' })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                <option value="gp3">gp3 (Recommended)</option>
                                <option value="gp2">gp2</option>
                                <option value="io1">io1 (High IOPS)</option>
                                <option value="io2">io2 (High IOPS)</option>
                              </select>
                            </div>
                            <div className="md:col-span-2 space-y-1">
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="useInstanceStore"
                                  checked={formData.gpuInstanceConfig.useInstanceStore}
                                  onChange={(e) => updateGpuConfig({ useInstanceStore: e.target.checked })}
                                  className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                                  disabled={formData.gpuInstanceConfig.instanceType.startsWith('p3') || formData.gpuInstanceConfig.instanceType.startsWith('t2') || formData.gpuInstanceConfig.instanceType.startsWith('t3')}
                                />
                                <label htmlFor="useInstanceStore" className="text-sm text-foreground-secondary">
                                  Use NVMe instance store (ephemeral high-speed storage)
                                </label>
                              </div>
                              {(formData.gpuInstanceConfig.instanceType.startsWith('p3') || formData.gpuInstanceConfig.instanceType.startsWith('t2') || formData.gpuInstanceConfig.instanceType.startsWith('t3')) && (
                                <div className="flex items-start gap-2 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                    {formData.gpuInstanceConfig.instanceType.startsWith('p3') 
                                      ? 'p3 instances are EBS-only and do not have NVMe instance store. Use g4dn or g5 instances for NVMe storage.' 
                                      : 'Free-tier instances do not have NVMe instance store.'}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Network */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Network className="w-4 h-4 text-accent" />
                            Network
                          </h4>
                          
                          {/* Network Mode Selection */}
                          <div className="mb-4">
                            <label className="block text-sm text-foreground-secondary mb-2">Network Mode</label>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ networkMode: 'shared', existingVpcId: '', existingSubnetId: '', existingSecurityGroupId: '' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-left transition-all',
                                  formData.gpuInstanceConfig.networkMode === 'shared'
                                    ? 'border-accent bg-accent/10'
                                    : 'border-border hover:border-accent/50 bg-background'
                                )}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Server className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium text-foreground">Shared</span>
                                </div>
                                <p className="text-xs text-foreground-tertiary">Platform's default VPC</p>
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ networkMode: 'dedicated', existingVpcId: '', existingSubnetId: '', existingSecurityGroupId: '' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-left transition-all',
                                  formData.gpuInstanceConfig.networkMode === 'dedicated'
                                    ? 'border-accent bg-accent/10'
                                    : 'border-border hover:border-accent/50 bg-background'
                                )}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Shield className="w-4 h-4 text-emerald-500" />
                                  <span className="text-sm font-medium text-foreground">Dedicated</span>
                                </div>
                                <p className="text-xs text-foreground-tertiary">Isolated VPC per instance</p>
                              </button>

                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ networkMode: 'existing' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-left transition-all',
                                  formData.gpuInstanceConfig.networkMode === 'existing'
                                    ? 'border-accent bg-accent/10'
                                    : 'border-border hover:border-accent/50 bg-background'
                                )}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Key className="w-4 h-4 text-orange-500" />
                                  <span className="text-sm font-medium text-foreground">Existing</span>
                                </div>
                                <p className="text-xs text-foreground-tertiary">Select existing network</p>
                              </button>
                            </div>
                          </div>

                          {/* Network mode descriptions */}
                          {formData.gpuInstanceConfig.networkMode === 'shared' && (
                            <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                              <p className="text-xs text-blue-600 dark:text-blue-400">
                                <strong>Shared Network:</strong> Uses the platform's default VPC. Multiple instances share the same network for cost efficiency.
                              </p>
                            </div>
                          )}
                          {formData.gpuInstanceConfig.networkMode === 'dedicated' && (
                            <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                                <strong>Dedicated Network:</strong> Creates a separate VPC for this instance. Provides complete network isolation. Auto-cleaned on termination.
                              </p>
                            </div>
                          )}
                          {formData.gpuInstanceConfig.networkMode === 'existing' && (
                            <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                              <p className="text-xs text-orange-600 dark:text-orange-400">
                                <strong>Existing Network:</strong> Use your own VPC, subnet, and security group. Useful for integrating with existing AWS infrastructure.
                              </p>
                            </div>
                          )}

                          {/* Existing Network Inputs */}
                          {formData.gpuInstanceConfig.networkMode === 'existing' && (
                            <div className="mb-4 p-4 rounded-lg border border-border bg-background space-y-3">
                              <div>
                                <label className="block text-sm text-foreground-secondary mb-1">VPC ID *</label>
                                <input
                                  type="text"
                                  value={formData.gpuInstanceConfig.existingVpcId}
                                  onChange={(e) => updateGpuConfig({ existingVpcId: e.target.value, existingSubnetId: '', existingSecurityGroupId: '' })}
                                  placeholder="vpc-0123456789abcdef0"
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-foreground-secondary mb-1">Subnet ID *</label>
                                <input
                                  type="text"
                                  value={formData.gpuInstanceConfig.existingSubnetId}
                                  onChange={(e) => updateGpuConfig({ existingSubnetId: e.target.value })}
                                  placeholder="subnet-0123456789abcdef0"
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm text-foreground-secondary mb-1">Security Group ID *</label>
                                <input
                                  type="text"
                                  value={formData.gpuInstanceConfig.existingSecurityGroupId}
                                  onChange={(e) => updateGpuConfig({ existingSecurityGroupId: e.target.value })}
                                  placeholder="sg-0123456789abcdef0"
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                            </div>
                          )}

                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Availability Zone</label>
                              <select
                                value={formData.gpuInstanceConfig.availabilityZone}
                                onChange={(e) => updateGpuConfig({ availabilityZone: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                {AVAILABILITY_ZONES.map((az) => (
                                  <option key={az.value} value={az.value}>{az.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="assignPublicIp"
                                checked={formData.gpuInstanceConfig.assignPublicIp}
                                onChange={(e) => updateGpuConfig({ assignPublicIp: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                              />
                              <label htmlFor="assignPublicIp" className="text-sm text-foreground-secondary">
                                Assign public IP address
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Cost & Lifecycle Controls */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-accent" />
                            Cost & Lifecycle
                          </h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Max Job Cost</label>
                              <div className="grid grid-cols-4 gap-1">
                                {MAX_COST_PRESETS.map((preset) => (
                                  <button
                                    key={preset.value}
                                    type="button"
                                    onClick={() => updateGpuConfig({ maxCostUsd: preset.value })}
                                    className={clsx(
                                      'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                      formData.gpuInstanceConfig.maxCostUsd === preset.value
                                        ? 'border-accent bg-accent/10 text-accent'
                                        : 'border-border hover:border-accent/50 text-foreground-secondary',
                                    )}
                                  >
                                    <div className="text-sm font-medium">{preset.label}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-foreground-secondary mb-2">Idle Timeout</label>
                              <div className="grid grid-cols-3 gap-1">
                                {IDLE_TIMEOUTS.map((timeout) => (
                                  <button
                                    key={timeout.value}
                                    type="button"
                                    onClick={() => updateGpuConfig({ idleTimeoutMinutes: timeout.value })}
                                    className={clsx(
                                      'px-2 py-2 rounded-lg border-2 text-center transition-all',
                                      formData.gpuInstanceConfig.idleTimeoutMinutes === timeout.value
                                        ? 'border-accent bg-accent/10 text-accent'
                                        : 'border-border hover:border-accent/50 text-foreground-secondary',
                                    )}
                                  >
                                    <div className="text-xs font-medium">{timeout.label}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            {formData.gpuInstanceConfig.useSpot && (
                              <>
                                <div>
                                  <label className="block text-sm text-foreground-secondary mb-2">Spot Interruption Behavior</label>
                                  <select
                                    value={formData.gpuInstanceConfig.spotInterruptionBehavior}
                                    onChange={(e) => updateGpuConfig({ spotInterruptionBehavior: e.target.value as 'terminate' | 'stop' | 'hibernate' })}
                                    className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                                  >
                                    <option value="terminate">Terminate instance</option>
                                    <option value="stop">Stop instance</option>
                                    <option value="hibernate">Hibernate instance</option>
                                  </select>
                                </div>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id="spotFallback"
                                    checked={formData.gpuInstanceConfig.spotFallbackToOnDemand}
                                    onChange={(e) => updateGpuConfig({ spotFallbackToOnDemand: e.target.checked })}
                                    className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                                  />
                                  <label htmlFor="spotFallback" className="text-sm text-foreground-secondary">
                                    Fall back to on-demand if spot unavailable
                                  </label>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Security */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Key className="w-4 h-4 text-accent" />
                            Security
                          </h4>
                          <div>
                            <label className="block text-sm text-foreground-secondary mb-2">SSH Key Management</label>
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ sshKeyMode: 'generate' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-center transition-all',
                                  formData.gpuInstanceConfig.sshKeyMode === 'generate'
                                    ? 'border-accent bg-accent/10 text-accent'
                                    : 'border-border hover:border-accent/50 text-foreground-secondary',
                                )}
                              >
                                <div className="text-sm font-medium">Generate</div>
                                <div className="text-xs text-foreground-tertiary mt-1">Auto-create key pair</div>
                              </button>
                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ sshKeyMode: 'existing' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-center transition-all',
                                  formData.gpuInstanceConfig.sshKeyMode === 'existing'
                                    ? 'border-accent bg-accent/10 text-accent'
                                    : 'border-border hover:border-accent/50 text-foreground-secondary',
                                )}
                              >
                                <div className="text-sm font-medium">Existing</div>
                                <div className="text-xs text-foreground-tertiary mt-1">Use existing key</div>
                              </button>
                              <button
                                type="button"
                                onClick={() => updateGpuConfig({ sshKeyMode: 'none' })}
                                className={clsx(
                                  'p-3 rounded-lg border-2 text-center transition-all',
                                  formData.gpuInstanceConfig.sshKeyMode === 'none'
                                    ? 'border-accent bg-accent/10 text-accent'
                                    : 'border-border hover:border-accent/50 text-foreground-secondary',
                                )}
                              >
                                <div className="text-sm font-medium">None</div>
                                <div className="text-xs text-foreground-tertiary mt-1">No SSH access</div>
                              </button>
                            </div>
                            {formData.gpuInstanceConfig.sshKeyMode === 'existing' && (
                              <div className="mt-3">
                                <input
                                  type="text"
                                  value={formData.gpuInstanceConfig.existingSshKeyName}
                                  onChange={(e) => updateGpuConfig({ existingSshKeyName: e.target.value })}
                                  placeholder="Enter existing SSH key name"
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Notifications */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Bell className="w-4 h-4 text-accent" />
                            Notifications
                          </h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.gpuInstanceConfig.notifyOnComplete}
                                onChange={(e) => updateGpuConfig({ notifyOnComplete: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                              />
                              <span className="text-sm text-foreground-secondary">Notify when job completes</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.gpuInstanceConfig.notifyOnFailure}
                                onChange={(e) => updateGpuConfig({ notifyOnFailure: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                              />
                              <span className="text-sm text-foreground-secondary">Notify on failure</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.gpuInstanceConfig.requireHeartbeat}
                                onChange={(e) => updateGpuConfig({ requireHeartbeat: e.target.checked })}
                                className="w-4 h-4 rounded border-border text-accent focus:ring-accent"
                              />
                              <span className="text-sm text-foreground-secondary">Require heartbeat monitoring</span>
                            </label>
                          </div>
                        </div>

                        {/* Metadata */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-accent" />
                            Metadata
                          </h4>
                          <div>
                            <label className="block text-sm text-foreground-secondary mb-2">Instance Name (optional)</label>
                            <input
                              type="text"
                              value={formData.gpuInstanceConfig.name}
                              onChange={(e) => updateGpuConfig({ name: e.target.value })}
                              placeholder="e.g., training-run-001"
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Info Banner */}
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-sm text-blue-400">
                        🌐 Each GPU container automatically gets its own dedicated network infrastructure 
                        (VPC, subnets, security groups, and routing). The network is deployed first, 
                        then your GPU instances are launched.
                      </p>
                    </div>

                    {/* Instance Type */}
                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-2">
                        <Cpu className="w-4 h-4 inline mr-1" />
                        Instance Type
                      </label>
                      <select
                        value={formData.containerInstanceType}
                        onChange={(e) => updateFormData({ containerInstanceType: e.target.value })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        {INSTANCE_TYPES.filter(t => !t.freeTier).map((type) => (
                          <option key={type.type} value={type.type}>
                            {type.gpu}{type.gpus > 1 ? ` (${type.gpus}x)` : ''} — {type.type} — ${type.serviceOnDemandPerHr.toFixed(2)}/hr
                          </option>
                        ))}
                      </select>
                      {formData.containerInstanceType.startsWith('p3') && (
                        <div className="mt-2 flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-yellow-600 dark:text-yellow-400">
                            p3 instances do not include NVMe instance storage. Only EBS volumes are available.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Max Instances */}
                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-2">
                        Max Instances
                      </label>
                      <input
                        type="number"
                        value={formData.maxInstances}
                        onChange={(e) => updateFormData({ maxInstances: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        min="1"
                        max="10"
                      />
                    </div>

                    {/* Initial Capacity */}
                    <div>
                      <label className="block text-sm font-medium text-foreground-secondary mb-2">
                        Initial Capacity
                      </label>
                      <input
                        type="number"
                        value={formData.desiredCount}
                        onChange={(e) => updateFormData({ desiredCount: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        min="0"
                        max={formData.maxInstances}
                      />
                      <p className="mt-1 text-xs text-foreground-tertiary">
                        Set to 0 for scale-to-zero (instances launch on demand)
                      </p>
                    </div>

                    {/* Use Spot Instances */}
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.useSpot}
                          onChange={(e) => updateFormData({ useSpot: e.target.checked })}
                          className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
                        />
                        <span className="text-sm font-medium text-foreground-secondary">
                          Use Spot Instances (up to 90% savings)
                        </span>
                      </label>
                    </div>

                    {/* Auto Scale-Down Settings */}
                    <div className="pt-4 border-t border-border">
                      <h4 className="text-sm font-semibold text-foreground mb-4">
                        Auto Scale-Down Settings
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Grace Period (seconds)
                          </label>
                          <input
                            type="number"
                            value={formData.gracePeriod}
                            onChange={(e) => updateFormData({ gracePeriod: parseInt(e.target.value) })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                            min="30"
                            step="30"
                          />
                          <p className="mt-1 text-xs text-foreground-tertiary">
                            Time to wait before scaling down idle cluster
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Check Interval (seconds)
                          </label>
                          <input
                            type="number"
                            value={formData.checkInterval}
                            onChange={(e) => updateFormData({ checkInterval: parseInt(e.target.value) })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                            min="30"
                            step="30"
                          />
                          <p className="mt-1 text-xs text-foreground-tertiary">
                            How often to check if cluster is idle
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Advanced Container Settings Toggle */}
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAdvancedContainer(!showAdvancedContainer)}
                        className="flex items-center gap-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                      >
                        {showAdvancedContainer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {showAdvancedContainer ? 'Hide' : 'Show'} Advanced Settings
                      </button>
                    </div>

                    {showAdvancedContainer && (
                      <div className="space-y-5 pt-2 border-t border-border">
                        {/* Docker Image */}
                        <div>
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            <Container className="w-4 h-4 inline mr-1" />
                            Docker Image
                          </label>
                          <select
                            value={formData.containerAdvanced.dockerImage}
                            onChange={(e) => updateContainerAdvanced({ dockerImage: e.target.value })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          >
                            {DOCKER_IMAGES.map((img) => (
                              <option key={img.value} value={img.value}>
                                {img.label} — {img.description}
                              </option>
                            ))}
                          </select>
                          {formData.containerAdvanced.dockerImage === 'custom' && (
                            <input
                              type="text"
                              value={formData.containerAdvanced.customDockerImage}
                              onChange={(e) => updateContainerAdvanced({ customDockerImage: e.target.value })}
                              placeholder="e.g., my-registry.com/my-image:latest"
                              className="w-full mt-2 px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          )}
                        </div>

                        {/* Startup Command */}
                        <div>
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            Startup Command (optional)
                          </label>
                          <input
                            type="text"
                            value={formData.containerAdvanced.startupCommand}
                            onChange={(e) => updateContainerAdvanced({ startupCommand: e.target.value })}
                            placeholder='e.g., python train.py --epochs 100'
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>

                        {/* Environment Variables */}
                        <div>
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            <Key className="w-4 h-4 inline mr-1" />
                            Environment Variables (KEY=VALUE, one per line)
                          </label>
                          <textarea
                            value={formData.containerAdvanced.environmentVariables}
                            onChange={(e) => updateContainerAdvanced({ environmentVariables: e.target.value })}
                            placeholder={'BATCH_SIZE=32\nLEARNING_RATE=0.001\nWANDB_API_KEY=your-key'}
                            rows={3}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent font-mono text-xs"
                          />
                        </div>

                        {/* Task Resources */}
                        <div className="pt-3 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-foreground mb-3">
                            <Cpu className="w-4 h-4 inline mr-1" />
                            Task Resources
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Task CPU</label>
                              <select
                                value={formData.containerAdvanced.taskCpu}
                                onChange={(e) => updateContainerAdvanced({ taskCpu: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                <option value="1024">1 vCPU</option>
                                <option value="2048">2 vCPUs</option>
                                <option value="4096">4 vCPUs</option>
                                <option value="8192">8 vCPUs</option>
                                <option value="16384">16 vCPUs</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Task Memory</label>
                              <select
                                value={formData.containerAdvanced.taskMemory}
                                onChange={(e) => updateContainerAdvanced({ taskMemory: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                <option value="8192">8 GB</option>
                                <option value="16384">16 GB</option>
                                <option value="32768">32 GB</option>
                                <option value="65536">64 GB</option>
                                <option value="131072">128 GB</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Port Mapping */}
                        <div className="pt-3 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-foreground mb-3">
                            <Network className="w-4 h-4 inline mr-1" />
                            Port Mapping
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Container Port</label>
                              <input
                                type="number"
                                min="1"
                                max="65535"
                                value={formData.containerAdvanced.containerPort}
                                onChange={(e) => updateContainerAdvanced({ containerPort: parseInt(e.target.value) || 8080 })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Host Port (0 = dynamic)</label>
                              <input
                                type="number"
                                min="0"
                                max="65535"
                                value={formData.containerAdvanced.hostPort}
                                onChange={(e) => updateContainerAdvanced({ hostPort: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Health Check */}
                        <div className="pt-3 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-foreground mb-3">
                            <Shield className="w-4 h-4 inline mr-1" />
                            Health Check
                          </h4>
                          <div>
                            <label className="block text-sm font-medium text-foreground-secondary mb-2">Health Check Path (leave empty to skip)</label>
                            <input
                              type="text"
                              value={formData.containerAdvanced.healthCheckPath}
                              onChange={(e) => updateContainerAdvanced({ healthCheckPath: e.target.value })}
                              placeholder="/health"
                              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          </div>
                          {formData.containerAdvanced.healthCheckPath && (
                            <div className="grid grid-cols-3 gap-4 mt-3">
                              <div>
                                <label className="block text-sm font-medium text-foreground-secondary mb-2">Interval (s)</label>
                                <input
                                  type="number"
                                  min="5"
                                  max="300"
                                  value={formData.containerAdvanced.healthCheckIntervalSeconds}
                                  onChange={(e) => updateContainerAdvanced({ healthCheckIntervalSeconds: parseInt(e.target.value) || 30 })}
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground-secondary mb-2">Timeout (s)</label>
                                <input
                                  type="number"
                                  min="2"
                                  max="120"
                                  value={formData.containerAdvanced.healthCheckTimeoutSeconds}
                                  onChange={(e) => updateContainerAdvanced({ healthCheckTimeoutSeconds: parseInt(e.target.value) || 5 })}
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-foreground-secondary mb-2">Retries</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={formData.containerAdvanced.healthCheckRetries}
                                  onChange={(e) => updateContainerAdvanced({ healthCheckRetries: parseInt(e.target.value) || 3 })}
                                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* CloudWatch Logging */}
                        <div className="pt-3 border-t border-border/50">
                          <label className="block text-sm font-medium text-foreground-secondary mb-2">
                            CloudWatch Log Retention
                          </label>
                          <select
                            value={formData.containerAdvanced.logRetentionDays}
                            onChange={(e) => updateContainerAdvanced({ logRetentionDays: parseInt(e.target.value) || 30 })}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                          >
                            <option value={1}>1 day</option>
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                            <option value={60}>60 days</option>
                            <option value={90}>90 days</option>
                            <option value={365}>1 year</option>
                          </select>
                        </div>

                        {/* Lifecycle & Cost Controls */}
                        <div className="pt-3 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-foreground mb-3">
                            <DollarSign className="w-4 h-4 inline mr-1" />
                            Lifecycle & Cost Controls
                          </h4>
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Idle Timeout</label>
                              <select
                                value={formData.containerAdvanced.idleTimeoutMinutes}
                                onChange={(e) => updateContainerAdvanced({ idleTimeoutMinutes: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                {IDLE_TIMEOUTS.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground-secondary mb-2">Max Cost (USD)</label>
                              <select
                                value={formData.containerAdvanced.maxCostUsd}
                                onChange={(e) => updateContainerAdvanced({ maxCostUsd: parseInt(e.target.value) || 0 })}
                                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                              >
                                {MAX_COST_PRESETS.map((c) => (
                                  <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.containerAdvanced.requireHeartbeat}
                                onChange={(e) => updateContainerAdvanced({ requireHeartbeat: e.target.checked })}
                                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
                              />
                              <span className="text-sm font-medium text-foreground-secondary">
                                <Zap className="w-3.5 h-3.5 inline mr-1" />
                                Require Heartbeat (terminate if client stops responding)
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* Notifications */}
                        <div className="pt-3 border-t border-border/50">
                          <h4 className="text-sm font-semibold text-foreground mb-3">
                            <Bell className="w-4 h-4 inline mr-1" />
                            Notifications
                          </h4>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.containerAdvanced.notifyOnComplete}
                                onChange={(e) => updateContainerAdvanced({ notifyOnComplete: e.target.checked })}
                                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
                              />
                              <span className="text-sm font-medium text-foreground-secondary">
                                Notify on job completion
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.containerAdvanced.notifyOnFailure}
                                onChange={(e) => updateContainerAdvanced({ notifyOnFailure: e.target.checked })}
                                className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-accent"
                              />
                              <span className="text-sm font-medium text-foreground-secondary">
                                Notify on failure
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Cost Summary */}
                <div className="mt-6 p-5 bg-accent/10 border border-accent/20 rounded-lg">
                  <div className="flex items-center gap-2 text-accent mb-4">
                    <DollarSign className="w-5 h-5" />
                    <span className="font-semibold text-lg">Cost Summary</span>
                  </div>
                  {(() => {
                    const isInstance = formData.deploymentType === 'instance';
                    const instanceTypeKey = isInstance
                      ? formData.gpuInstanceConfig.instanceType
                      : formData.containerInstanceType;
                    const useSpot = isInstance
                      ? formData.gpuInstanceConfig.useSpot
                      : formData.useSpot;
                    const inst = INSTANCE_TYPES.find(t => t.type === instanceTypeKey);

                    if (!inst) return <p className="text-sm text-foreground-secondary">Select an instance type</p>;

                    if (inst.freeTier) {
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-foreground">{inst.type}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-medium">FREE TIER</span>
                          </div>
                          <p className="text-sm text-emerald-600 dark:text-emerald-400">No compute charges — free tier eligible</p>
                        </div>
                      );
                    }

                    const hourly = useSpot ? inst.serviceSpotPerHr : inst.serviceOnDemandPerHr;
                    const monthly = hourly * HOURS_PER_MONTH;
                    const altHourly = useSpot ? inst.serviceOnDemandPerHr : inst.serviceSpotPerHr;
                    const altMonthly = altHourly * HOURS_PER_MONTH;
                    const maxInst = isInstance ? 1 : formData.maxInstances;
                    const maxRuntime = isInstance ? formData.gpuInstanceConfig.maxRuntimeHours : null;
                    const sessionCost = maxRuntime ? hourly * maxRuntime : null;
                    const savingsPct = useSpot
                      ? Math.round((1 - inst.serviceSpotPerHr / inst.serviceOnDemandPerHr) * 100)
                      : 0;

                    return (
                      <div className="space-y-4">
                        {/* Instance identifier */}
                        <div className="text-sm text-foreground-secondary">
                          <span className="font-semibold text-foreground">{inst.gpu || 'CPU Only'}</span>
                          {inst.gpus > 1 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-accent/20 text-accent font-medium">{inst.gpus}x</span>}
                          <span className="ml-2 font-mono text-xs text-foreground-tertiary">{inst.type}</span>
                          {' · '}{inst.gpuMemory > 0 ? `${inst.gpuMemory} GB VRAM · ` : ''}{inst.vCpus} vCPU · {inst.memory} GB RAM
                          {useSpot && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-medium">
                              Spot
                            </span>
                          )}
                          {!useSpot && (
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-accent/20 text-accent font-medium">
                              On-Demand
                            </span>
                          )}
                        </div>

                        {/* Per hour / per month */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-background rounded-lg border border-border">
                            <div className="text-xs text-foreground-tertiary uppercase tracking-wider">Per Hour</div>
                            <div className="text-xl font-bold text-foreground mt-1">
                              ${hourly.toFixed(2)}
                              <span className="text-sm font-normal text-foreground-secondary">/hr</span>
                            </div>
                            {useSpot && savingsPct > 0 && (
                              <div className="text-xs text-emerald-500 mt-1">~{savingsPct}% vs on-demand</div>
                            )}
                          </div>
                          <div className="p-3 bg-background rounded-lg border border-border">
                            <div className="text-xs text-foreground-tertiary uppercase tracking-wider">Per Month (est.)</div>
                            <div className="text-xl font-bold text-foreground mt-1">
                              ${monthly.toFixed(2)}
                              <span className="text-sm font-normal text-foreground-secondary">/mo</span>
                            </div>
                            <div className="text-xs text-foreground-tertiary mt-1">Based on 730h/mo</div>
                          </div>
                        </div>

                        {/* Max session cost (instance deployments) */}
                        {sessionCost !== null && (
                          <div className="p-3 bg-background rounded-lg border border-border flex items-center justify-between">
                            <div>
                              <div className="text-xs text-foreground-tertiary uppercase tracking-wider">Max Session Cost</div>
                              <div className="text-lg font-bold text-foreground mt-1">${sessionCost.toFixed(2)}</div>
                            </div>
                            <div className="text-sm text-foreground-tertiary">{maxRuntime}h max runtime</div>
                          </div>
                        )}

                        {/* Multi-instance total (container deployments) */}
                        {!isInstance && maxInst > 1 && (
                          <div className="p-3 bg-background rounded-lg border border-border">
                            <div className="text-xs text-foreground-tertiary uppercase tracking-wider mb-2">
                              At Max Capacity ({maxInst} instances)
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="text-lg font-bold text-foreground">
                                ${(hourly * maxInst).toFixed(2)}<span className="text-sm font-normal text-foreground-secondary">/hr</span>
                              </div>
                              <div className="text-lg font-bold text-foreground">
                                ${(monthly * maxInst).toFixed(2)}<span className="text-sm font-normal text-foreground-secondary">/mo</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Scale-to-zero note */}
                        {!isInstance && formData.desiredCount === 0 && (
                          <p className="text-xs text-emerald-500 italic">
                            Scale-to-zero enabled — no charges when idle
                          </p>
                        )}

                        {/* Reference pricing */}
                        <div className="pt-3 border-t border-accent/20 text-xs text-foreground-tertiary space-y-1">
                          <p>
                            {useSpot
                              ? `On-demand rate: $${altHourly.toFixed(2)}/hr · ~$${altMonthly.toFixed(2)}/mo`
                              : `Spot rate (est.): $${altHourly.toFixed(2)}/hr · ~$${altMonthly.toFixed(2)}/mo`}
                          </p>
                          <p>AWS base: ${inst.awsOnDemandPerHr.toFixed(3)}/hr on-demand</p>
                          {useSpot && <p>Spot pricing varies with AWS capacity and demand</p>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <button
            onClick={step === 1 ? onClose : handleBack}
            className="px-4 py-2 border border-border rounded-lg text-foreground-secondary hover:bg-background-hover transition-colors"
            disabled={isSubmitting}
          >
            {step === 1 ? (
              'Cancel'
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 inline mr-1" />
                Back
              </>
            )}
          </button>
          
          <button
            onClick={() => {
              if (step === totalSteps) {
                if (billingStatus && !billingStatus.canDeploy) {
                  setShowSubscriptionModal(true);
                  return;
                }
                handleSubmit();
              } else {
                handleNext();
              }
            }}
            disabled={!canProceed() || isSubmitting}
            className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {formData.deploymentType === 'instance' ? 'Launching Instance...' : 'Creating...'}
              </>
            ) : step === totalSteps ? (
              <>
                {formData.deploymentType === 'instance' ? (
                  <>
                    <Zap className="w-4 h-4" />
                    Launch Instance
                  </>
                ) : (
                  'Create Deployment'
                )}
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
